import React, { useState, useEffect, useContext } from 'react';
import '../../../App.scss';
import 'antd/dist/antd.css';
import { Link, useParams, useLocation } from 'react-router-dom';
import { PageHeader, Result, Button, Typography } from 'antd';

import TeamProfileScreenBody from '../../../components/client/TeamProfileScreenBody';
import { useTeamDetails } from '../../../components/client/useTeamDetails';
import { useTeamMembers } from '../../../components/client/useTeamMembers';
import CenteredSpinner from '../../../components/CenteredSpinner';
import { AdminCardForTeam } from '../../../components/admin/AdminCardForTeam';
import TeamAdminOnly from '../../../components/client/TeamAdminOnly';
import TeamUnderConstruction, { shouldShowTeamSetup } from '../../../components/client/TeamUnderConstruction';
import FasterSigninCard from '../../../components/client/FasterSigninCard';
import HomeScreenCard from '../../../components/client/HomeScreenCard';

const TeamProfileScreen = ({
  locStrings,
  isAdmin,
}) => {
  const { teamId, clientId } = useParams();
  const location = useLocation();
  const team = useTeamDetails(teamId);
  const teamMembers = useTeamMembers(teamId, team, clientId);
  // Both listeners must have reported before readiness can be judged. The team
  // doc arrives first, and a members list that has not loaded yet (null) looks
  // exactly like a team with no members.
  const membersLoaded = Array.isArray(teamMembers);
  const teamDocLoaded = !!(team && teamId && clientId) && membersLoaded;
  const needsSetup = teamDocLoaded && shouldShowTeamSetup(team, teamMembers, clientId);

  // Team creation is several triggers landing independently, so this tracks live
  // field updates rather than deciding once from a snapshot of a half-built team.
  //
  // The sequence starts if the team is ever seen incomplete, and then runs to its
  // end — otherwise the last field arriving would cut the finish off mid-play.
  // A team that is already complete when it loads never starts it at all, so
  // opening an existing team goes straight to its content.
  const [sequenceStarted, setSequenceStarted] = useState(false);
  const [sequenceFinished, setSequenceFinished] = useState(false);

  useEffect(() => {
    if (needsSetup && !sequenceStarted) setSequenceStarted(true);
  }, [needsSetup, sequenceStarted]);

  const showSetup = sequenceStarted && !sequenceFinished;
  const loading = !teamDocLoaded || showSetup;

  // Both listeners report errors to the console and never resolve, so a denied
  // or failed read leaves the screen spinning with nothing to act on. Give it a
  // deadline and say so instead.
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  useEffect(() => {
    if (teamDocLoaded) return undefined;
    const timer = setTimeout(() => setLoadTimedOut(true), 15000);
    return () => clearTimeout(timer);
  }, [teamDocLoaded]);
  const { teamClientId } = team || {};

  // Scroll to top when component mounts, route changes, or after loading completes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Also scroll to top after content finishes loading to override browser scroll restoration
  useEffect(() => {
    if (!loading) {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
      });
    }
  }, [loading]);

  return <>
      {
        <PageHeader
          ghost={false}
          title="Back"
          onBack={(() => window.history.back())}
        />
      }
      {/* A team page is for the people on that team. Everything below — name,
          logo, QR, activity, the Leave and Tip actions — was previously rendered
          for anyone who typed the URL. Strike admins still pass. */}
      <TeamAdminOnly teamId={teamId} require="member">
      { !loading && teamClientId && isAdmin &&
        <AdminCardForTeam
          isAdmin={isAdmin}
          clientId={teamClientId}
          team={team}
          locStrings={locStrings}
        />
      }
      {/* Right after a join is the one moment to offer a free sign-in for next
          time; the card shows only to a phone-only account and hides for good
          on "Not now". */}
      {!loading && !!(location.state && location.state.justJoined) && (
        <div style={{ padding: '16px 16px 0', maxWidth: 600, margin: '0 auto' }}>
          <FasterSigninCard />
          <HomeScreenCard />
        </div>
      )}
      {!loading &&
        <>
          <TeamProfileScreenBody
            locStrings={locStrings}
            teamId={teamId}
            team={team}
            clientId={clientId}
            teamMembers={teamMembers}
            isAdmin={isAdmin}
          />
        </>
      }
      {/* Distinguish "still fetching" from "still being built": the second is a
          normal state for a team created seconds ago, and deserves an answer
          rather than an indefinite spinner. */}
      {showSetup &&
        <TeamUnderConstruction
          team={team}
          teamMembers={teamMembers}
          clientId={clientId}
          teamName={team.teamName}
          onFinished={() => setSequenceFinished(true)}
        />
      }
      {/* The team doc lands before the roster, so name the team as soon as we
          know it rather than showing an anonymous spinner. */}
      {!teamDocLoaded && !loadTimedOut &&
        <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
          <CenteredSpinner loading={true} noLayout />
          <div style={{ marginTop: 16 }}>
            <Typography.Text type="secondary">
              {team && team.teamName ? `Opening ${team.teamName}…` : 'Opening your team…'}
            </Typography.Text>
          </div>
        </div>
      }
      {!teamDocLoaded && loadTimedOut &&
        <Result
          status="warning"
          title="We couldn't load this team"
          subTitle="The connection may have dropped. Reload the page, and let us know if it keeps happening."
          extra={<Button type="primary" onClick={() => window.location.reload()}>Reload</Button>}
        />
      }
      </TeamAdminOnly>
    </>

};


export default TeamProfileScreen;

